import React, { useEffect, useState } from 'react'
import { Link, useParams, useHistory } from 'react-router-dom'
import {
  Box,
  Container,
  Text,
  Stack,
  Table,
  Button,
  Group,
  Loader,
  Center,
  Modal,
  TextInput,
  Tabs,
  ActionIcon,
  Badge
} from '@mantine/core'
import { format, formatDistanceToNow } from 'date-fns'
import agent from '../../app/api/agent'
import { AmmoProductDto } from '../../app/models/ammo'
import { PostDto } from '../../app/models/post'
import { ContentPhotoDto, ContentPhotoOwnerType } from '../../app/models/contentPhoto'
import { useStore } from '../../app/stores/store'
import { observer } from 'mobx-react-lite'
import { toast } from 'react-toastify'
import { AmmoScoreBadge, POST_TYPE_LABELS, POST_TYPE_COLORS } from '../../app/common/ui'
import PhotoGallery from '../builds/PhotoGallery'
import GalleryUploadButton from '../builds/GalleryUploadButton'

const VOTE_BTN = {
  background: 'var(--gs-surface2)',
  border: '1.5px solid var(--gs-border-bright)',
  color: 'var(--gs-text)'
}

const AmmoProductDetail = observer(() => {
  const { id } = useParams<{ id: string }>()
  const history = useHistory()
  const { userStore, voteStore } = useStore()
  const [product, setProduct] = useState<AmmoProductDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [reviews, setReviews] = useState<PostDto[]>([])
  const [reviewsLoading, setReviewsLoading] = useState(false)
  const [photos, setPhotos] = useState<ContentPhotoDto[]>([])
  const [photosLoading, setPhotosLoading] = useState(false)
  const [displayScore, setDisplayScore] = useState(0)
  const [displayVote, setDisplayVote] = useState<number | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [showRejectModal, setShowRejectModal] = useState(false)

  useEffect(() => {
    agent.AmmoProducts.details(id)
      .then(p => {
        setProduct(p)
        setDisplayScore(p.voteScore ?? 0)
        setDisplayVote(p.currentUserVote ?? null)
      })
      .finally(() => setLoading(false))
  }, [id])

  useEffect(() => {
    if (!id) return
    setReviewsLoading(true)
    agent.Posts.list({ ammoProductId: id, type: 1, pageSize: 20 })
      .then(result => {
        setReviews(result.items)
      })
      .catch(() => setReviews([]))
      .finally(() => setReviewsLoading(false))
  }, [id])

  useEffect(() => {
    if (!id) return
    setPhotosLoading(true)
    agent.ContentPhotos.list(ContentPhotoOwnerType.AmmoProduct, id)
      .then(setPhotos)
      .catch(() => setPhotos([]))
      .finally(() => setPhotosLoading(false))
  }, [id])

  const handleApprove = async () => {
    if (!product) return
    try {
      await agent.AmmoProducts.approve(product.id)
      toast.success('Ammo product approved')
      setProduct((prev) => (prev ? { ...prev, isActive: true } : null))
    } catch (error) {
      toast.error('Failed to approve')
    }
  }

  const handleReject = async () => {
    if (!product) return
    try {
      await agent.AmmoProducts.reject(product.id, rejectReason)
      toast.success('Ammo product rejected')
      setShowRejectModal(false)
      setProduct((prev) => (prev ? { ...prev, isActive: false } : null))
    } catch (error) {
      toast.error('Failed to reject')
    }
  }

  const handleVote = async (value: number) => {
    if (!product || !id) return
    if (!userStore.isLoggedIn) {
      history.push('/register')
      return
    }
    const prevScore = displayScore
    const prevVote = displayVote
    const delta = value === displayVote ? -value : value - (displayVote ?? 0)
    setDisplayScore((s) => s + delta)
    setDisplayVote(value === displayVote ? null : value)
    try {
      if (value === displayVote) {
        await voteStore.removeVote(7, id)
      } else {
        await voteStore.castVote(7, id, value)
      }
    } catch {
      setDisplayScore(prevScore)
      setDisplayVote(prevVote)
    }
  }

  const handleGalleryDelete = async (photoId: string) => {
    await agent.ContentPhotos.delete(photoId)
    setPhotos((prev) => prev.filter((p) => p.id !== photoId))
  }

  const handleGallerySetMain = async (photoId: string) => {
    await agent.ContentPhotos.setMain(photoId)
    if (id) {
      const list = await agent.ContentPhotos.list(ContentPhotoOwnerType.AmmoProduct, id)
      setPhotos(list)
    }
  }

  const handleGalleryUploadSuccess = (photo: ContentPhotoDto) => {
    setPhotos((prev) => [...prev, photo])
  }

  if (loading) {
    return (
      <Center>
        <Loader />
      </Center>
    )
  }

  if (!product) { return <Text>Product not found</Text> }

  return (
    <Box sx={{ background: 'var(--gs-bg)', minHeight: '100vh', paddingTop: '24px', paddingBottom: '24px' }}>
      <Container size="md">
        <Stack spacing="md">
          {/* Back Link */}
          <Button
            variant="subtle"
            onClick={() => history.push('/ammo')}
            sx={{ width: 'fit-content', paddingLeft: 0 }}
          >
            ← Back to Ammo Registry
          </Button>

          {/* Header */}
          <Box>
            <Text
              sx={{
                fontSize: '28px',
                fontWeight: 700,
                marginBottom: '4px'
              }}
            >
              {product.brand} {product.name}
            </Text>
            <Text
              sx={{
                color: 'var(--gs-muted)',
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: '14px'
              }}
            >
              {product.cartridgeSpecName}
            </Text>
          </Box>

          {/* Spec Table */}
          <Box sx={{ border: '1px solid var(--gs-border)', borderRadius: '6px', overflow: 'hidden' }}>
            <Table>
              <tbody>
                <tr style={{ borderBottom: '1px solid var(--gs-border)' }}>
                  <td style={{ padding: '12px', background: 'var(--gs-surface)', fontWeight: 700, width: '200px' }}>
                    Bullet Weight
                  </td>
                  <td style={{ padding: '12px', fontFamily: 'JetBrains Mono, monospace' }}>
                    {product.grainWeight ? `${product.grainWeight}gr` : '—'}
                  </td>
                </tr>
                <tr style={{ borderBottom: '1px solid var(--gs-border)' }}>
                  <td style={{ padding: '12px', background: 'var(--gs-surface)', fontWeight: 700 }}>
                    Bullet Type
                  </td>
                  <td style={{ padding: '12px', fontFamily: 'JetBrains Mono, monospace' }}>
                    {product.bulletType}
                  </td>
                </tr>
              </tbody>
            </Table>
          </Box>

          {/* Compatible Firearms Link */}
          <Box
            sx={{
              padding: '12px',
              background: 'var(--gs-surface)',
              border: '1px solid var(--gs-border)',
              borderRadius: '6px',
              cursor: 'pointer',
              '&:hover': { borderColor: '#1A3A5C' }
            }}
            onClick={() => history.push(`/ammo/specs/${product.cartridgeSpecId}`)}
          >
            <Text size="sm">
              This is a {product.cartridgeSpecName} load.{' '}
              <Text
                component="span"
                sx={{ color: 'var(--gs-accent)', fontWeight: 500 }}
              >
                View compatible firearms →
              </Text>
            </Text>
          </Box>

          {/* Voting Section */}
          <Group spacing="xs">
            <ActionIcon
              size="md"
              variant="subtle"
              sx={{ ...VOTE_BTN, ...(displayVote === 1 ? { borderColor: 'var(--gs-accent)', color: 'var(--gs-accent)' } : {}) }}
              onClick={() => handleVote(1)}
              aria-label="Upvote"
            >
              <Text weight={800} size="md" sx={{ lineHeight: 1 }}>
                +
              </Text>
            </ActionIcon>
            <Text sx={{ minWidth: 28, textAlign: 'center', fontWeight: 600 }}>{displayScore}</Text>
            <ActionIcon
              size="md"
              variant="subtle"
              sx={{ ...VOTE_BTN, ...(displayVote === -1 ? { borderColor: 'var(--gs-accent)', color: 'var(--gs-accent)' } : {}) }}
              onClick={() => handleVote(-1)}
              aria-label="Downvote"
            >
              <Text weight={800} size="md" sx={{ lineHeight: 1 }}>
                −
              </Text>
            </ActionIcon>
          </Group>

          {/* Admin Panel */}
          {userStore.user?.isAdmin && (
            <Box
              sx={{
                padding: '16px',
                background: 'var(--gs-surface2)',
                border: '1px solid var(--gs-border)',
                borderRadius: '6px'
              }}
            >
              <Text weight={700} size="sm" sx={{ marginBottom: '12px' }}>
                Admin Actions
              </Text>
              <Group spacing="sm">
                <Button size="sm" onClick={handleApprove} disabled={product.isActive}>
                  Approve
                </Button>
                <Button
                  size="sm"
                  color="red"
                  onClick={() => setShowRejectModal(true)}
                  disabled={!product.isActive}
                >
                  Reject
                </Button>
              </Group>
            </Box>
          )}

          {/* Community Photo Gallery */}
          {id && (
            <Box>
              <Group position="apart" align="center" mb="sm" sx={{ flexWrap: 'wrap' }}>
                <Text sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 600, fontSize: 14, color: 'var(--gs-text)' }}>
                  COMMUNITY PHOTOS
                </Text>
                {userStore.isLoggedIn && (
                  <GalleryUploadButton
                    ownerType={ContentPhotoOwnerType.AmmoProduct}
                    ownerId={id}
                    canUpload={true}
                    onSuccess={handleGalleryUploadSuccess}
                    label="+ Add Photo"
                  />
                )}
              </Group>
              {photosLoading
                ? (
                <Center py="lg">
                  <Loader size="sm" />
                </Center>
                  )
                : photos.length > 0
                  ? (
                <PhotoGallery
                  variant="ammoProduct"
                  photos={photos}
                  canManage={userStore.user?.isAdmin}
                  onDelete={handleGalleryDelete}
                  onSetMain={handleGallerySetMain}
                />
                    )
                  : (
                <Box
                  sx={{
                    padding: '24px',
                    textAlign: 'center',
                    border: '1px dashed var(--gs-border)',
                    borderRadius: '6px'
                  }}
                >
                  <Text sx={{ color: 'var(--gs-muted)' }}>
                    No photos yet. Be the first to share!
                  </Text>
                </Box>
                    )}
            </Box>
          )}

          {/* Reviews Section */}
          <Box>
            <Text weight={700} size="lg" sx={{ marginBottom: '12px' }}>
              Community Reviews
            </Text>
            {reviewsLoading
              ? (
              <Center py="lg">
                <Loader size="sm" />
              </Center>
                )
              : reviews.length > 0
                ? (
              <Stack spacing="md">
                {reviews.map((review) => (
                  <Box
                    key={review.id}
                    component={Link}
                    to={`/blog/${review.id}`}
                    sx={{
                      padding: '16px',
                      border: '1px solid var(--gs-border)',
                      borderRadius: '6px',
                      background: 'var(--gs-card)',
                      textDecoration: 'none',
                      transition: 'border-color 0.15s',
                      '&:hover': { borderColor: 'var(--gs-accent)' }
                    }}
                  >
                    <Group align="flex-start" spacing="sm" noWrap position="apart" mb="xs">
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Text
                          size="sm"
                          weight={600}
                          sx={{ color: 'var(--gs-text)' }}
                          lineClamp={2}
                        >
                          {review.title}
                        </Text>
                        <Group spacing="xs" mt="xs">
                          <Text component={Link} to={`/profiles/${review.authorUsername}`} size="xs" weight={500} sx={{ color: 'var(--gs-accent)' }}>
                            {review.authorDisplayName || review.authorUsername}
                          </Text>
                          <AmmoScoreBadge tier={review.authorAmmoTier ?? 'Newcomer'} score={review.authorAmmoScore ?? 0} size="xs" />
                          <Text size="xs" color="dimmed">
                            {formatDistanceToNow(new Date(review.createdAt), { addSuffix: true })}
                          </Text>
                        </Group>
                      </Box>
                      {review.rating && (
                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                          <Text size="sm" weight={700} sx={{ color: 'var(--gs-accent)' }}>
                            {review.rating}/5
                          </Text>
                        </Box>
                      )}
                    </Group>
                    {review.excerpt && (
                      <Text size="sm" color="dimmed" lineClamp={2} mt="xs">
                        {review.excerpt}
                      </Text>
                    )}
                  </Box>
                ))}
              </Stack>
                  )
                : (
              <Box
                sx={{
                  padding: '24px',
                  textAlign: 'center',
                  border: '1px dashed var(--gs-border)',
                  borderRadius: '6px'
                }}
              >
                <Text sx={{ color: 'var(--gs-muted)' }}>
                  No reviews yet. Be the first to share your experience!
                </Text>
              </Box>
                  )}
          </Box>
        </Stack>
      </Container>

      {/* Reject Modal */}
      <Modal
        opened={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        title="Reject Ammo Product"
      >
        <Stack spacing="md">
          <TextInput
            label="Reason (optional)"
            placeholder="Why is this being rejected?"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.currentTarget.value)}
          />
          <Group position="right">
            <Button variant="subtle" onClick={() => setShowRejectModal(false)}>
              Cancel
            </Button>
            <Button color="red" onClick={handleReject}>
              Reject
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  )
})

export default AmmoProductDetail
